// ConfirmDialog — the confirmation step for a destructive action.
//
// Replaces window.confirm, which was doing this job in three places. That was
// wrong for reasons beyond looks:
//
//   * it BLOCKS the main thread, so nothing renders behind it and the app is
//     frozen until the browser dialog is dismissed;
//   * it cannot say what will actually happen — "Delete the catalog?" gave no
//     hint that the products inside survive, which is the one thing an operator
//     needs to know before clicking;
//   * it is unstyled and unbrandable, and in some browsers can be suppressed
//     entirely, in which case the action fires with NO confirmation at all;
//   * it cannot show progress, so a slow delete looked like nothing happened.
//
// The consequence line is the point of this component. A confirm that only
// restates the title teaches an operator to click through it.
import { useEffect, useRef } from "react";

import { Modal } from "@/components/UI";

export interface ConfirmRequest {
  title: string;
  /** What will happen, in plain language. Shown above the buttons. */
  body: string;
  /** Label for the confirming button. Name the ACTION ("Delete product"), not "OK". */
  confirmLabel: string;
  /** Marks the action as destructive; styles the confirm button accordingly. */
  destructive?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({
  request,
  busy,
  onClose,
}: {
  request: ConfirmRequest | null;
  busy?: boolean;
  onClose: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Focus CANCEL, not confirm, when a destructive dialog opens: a stray Enter
  // held over from the click that opened it must not commit the deletion.
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!request) return;
    const target = request.destructive ? cancelRef : confirmRef;
    target.current?.focus();
  }, [request]);

  if (!request) return null;

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      busy={busy}
      title={request.title}
      foot={
        <>
          <button
            ref={cancelRef}
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            ref={confirmRef}
            className={request.destructive ? "btn danger" : "btn accent"}
            type="button"
            disabled={busy}
            onClick={request.onConfirm}
          >
            {busy ? "Working…" : request.confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ fontSize: 13, lineHeight: 1.55 }}>{request.body}</div>
    </Modal>
  );
}
